<template>
  <header class="pl-topbar pl-shell-header" :class="{ 'pl-shell-header--ambient': isAmbient }">
    <!-- The mark is the door out. It used to be a line of grey type that read
         as a caption and, worse, led nowhere: the landing route bounced a
         signed-in player straight back here. Now it is the same brand object
         the public header carries, and it opens the same page. -->
    <router-link
      class="pl-shell-header__brand"
      :to="{ name: 'Home' }"
      :aria-label="t('navigation.brandHome')"
    >
      <BrandMark class="pl-shell-header__mark" />
      <span class="pl-shell-header__wordmark">Dimension&#8209;X</span>
    </router-link>

    <nav class="pl-shell-header__nav" :aria-label="t('navigation.primary')">
      <!-- On the lobby itself this used to be a link back to the lobby: a
           button that reads as an invitation and does nothing when pressed.
           Where it leads nowhere it stops being a control and becomes the
           label for where you already are. -->
      <span v-if="isLobby" class="pl-chip pl-shell-header__here" aria-current="page">
        {{ t('navigation.lobby') }}
      </span>
      <router-link
        v-else
        class="pl-btn pl-btn--ghost pl-btn--sm"
        :to="{ name: 'PlayerHomeDashboard' }"
      >
        {{ t('navigation.lobby') }}
      </router-link>
      <router-link
        v-if="isGameMaster"
        class="pl-btn pl-btn--ghost pl-btn--sm"
        :to="{ name: 'GameMaster' }"
      >
        {{ t('navigation.gameMaster') }}
      </router-link>
    </nav>

    <!-- Everything that is not the game: the rest of the site, the language,
         and whoever is signed in. Signing in used to remove all three. -->
    <div class="pl-shell-header__aside">
      <SiteMenu />
      <LanguageSwitcher class="pl-shell-header__lang" />
    </div>

    <div ref="root" class="pl-topbar__actions pl-shell-header__account">
      <!-- The portrait is the last thing in the bar and the only round thing
           in it, so the eye finds "me" without reading anything. The name
           leads into it left to right, the way a signature does. -->
      <button
        class="pl-btn pl-btn--ghost pl-btn--sm pl-shell-header__id"
        type="button"
        :aria-expanded="open ? 'true' : 'false'"
        aria-haspopup="menu"
        @click="open = !open"
      >
        <span class="pl-shell-header__name">{{ label }}</span>
        <span class="pl-shell-header__face">
          <img
            v-if="avatar"
            class="pl-avatar pl-avatar--sm pl-shell-header__portrait"
            :src="avatar"
            alt=""
            aria-hidden="true"
            @error="avatarFailed = true"
          />
          <span
            v-else
            class="pl-avatar pl-avatar--sm pl-avatar--monogram pl-shell-header__portrait"
            aria-hidden="true"
          >{{ initial }}</span>
        </span>
      </button>

      <ul v-if="open" class="pl-panel pl-panel--raised pl-menu pl-shell-header__menu" role="menu">
        <li role="none">
          <router-link
            class="pl-menu__item"
            role="menuitem"
            :to="{ name: 'CharacterPreview' }"
            @click="open = false"
          >{{ t('navigation.characterPreview') }}</router-link>
        </li>

        <!-- On a phone the bar has no room for a fifth control, so the site
             and the language fold in here rather than being dropped. Above
             700px these rows are hidden and .pl-shell-header__aside carries
             them instead. -->
        <li class="pl-menu__sep pl-shell-header__folded" role="separator"></li>
        <li
          v-for="link in siteLinks"
          :key="link.id"
          class="pl-shell-header__folded"
          role="none"
        >
          <router-link
            class="pl-menu__item"
            role="menuitem"
            :to="link.url"
            @click="open = false"
          >{{ t(`navigation.${link.id}`) }}</router-link>
        </li>
        <li class="pl-shell-header__folded pl-shell-header__lang-row" role="none">
          <LanguageSwitcher class="pl-shell-header__lang" />
        </li>

        <li class="pl-menu__sep" role="separator"></li>
        <li role="none">
          <button
            class="pl-menu__item"
            role="menuitem"
            type="button"
            @click="logout"
          >{{ t('navigation.logout') }}</button>
        </li>
      </ul>
    </div>
  </header>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import ClientManagerService from '@/services/clientManagerService.js';
import BrandMark from '@/components/BrandMark.vue';
import LanguageSwitcher from '@/components/LanguageSwitcher.vue';
import SiteMenu from '@/components/Player/SiteMenu.vue';
import { SITE_LINKS } from '@/components/Player/siteLinks.js';
import { mediaUrl } from '@/components/Player/mediaUrl.js';

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const route = useRoute();

const open = ref(false);
const identity = ref(null);
const avatarFailed = ref(false);
const root = ref(null);

const isGameMaster = computed(() => store.getters.isGameMaster);

/* Routes whose page is a .pl-page--ambient. The menu bar sits above that
   page's fixed picture layer, so on these routes it goes translucent and lets
   the room run to the top of the window instead of capping it with a slab. */
const AMBIENT_ROUTES = ['PlayerHomeDashboard', 'CharacterSubmit'];
const isAmbient = computed(() => AMBIENT_ROUTES.includes(route.name));

const isLobby = computed(() => route.name === 'PlayerHomeDashboard');

const siteLinks = SITE_LINKS;

/* Who is signed in, said as plainly as the account can say it: the name they
   registered under, else the address they sign in with. `navigation.account`
   is the last resort and names nobody — before the identity call lands, and
   after it fails. */
const label = computed(
  () => identity.value?.display_name
    || identity.value?.first_name
    || identity.value?.email
    || t('navigation.account')
);
const initial = computed(() => (label.value || '?').trim().charAt(0).toUpperCase());
const avatar = computed(
  () => (avatarFailed.value ? '' : mediaUrl(identity.value?.avatar ?? ''))
);

const loadIdentity = async () => {
  if (!store.getters.isAuthenticated) {
    identity.value = null;
    return;
  }
  try {
    identity.value = await ClientManagerService.getCurrentClientInfo();
    avatarFailed.value = false;
  } catch (error) {
    identity.value = null;
  }
};

const logout = () => {
  open.value = false;
  ClientManagerService.clearClientInfoCache();
  store.dispatch('logout');
  identity.value = null;
  router.push({ name: 'Home' });
};

const onDocumentClick = (event) => {
  if (open.value && root.value && !root.value.contains(event.target)) open.value = false;
};
const onKeydown = (event) => {
  if (event.key === 'Escape') open.value = false;
};

watch(() => route.fullPath, () => { open.value = false; });
watch(() => store.getters.isAuthenticated, loadIdentity);

onMounted(() => {
  document.addEventListener('click', onDocumentClick);
  document.addEventListener('keydown', onKeydown);
  loadIdentity();
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick);
  document.removeEventListener('keydown', onKeydown);
});
</script>

<style scoped>
/* One height for everything in the bar. Three controls at three heights — an
   18px chip, a 25px button and a 48px account block — is what made the row
   read as a pile rather than as a line. */
.pl-shell-header {
  --pl-bar-control: 2.25rem;
  position: relative;
  z-index: var(--z-chrome);
  align-items: stretch;
  min-height: calc(var(--pl-bar-control) + 2 * var(--pl-gap));
}

/* :link/:visited and font-style for the same reason as the nav rule below —
   global.css italicises every anchor and repaints visited ones. An oblique
   Orbitron is a synthesised slant, which is what made the wordmark sit at a
   different angle from the mark beside it. */
.pl-shell-header__brand,
.pl-shell-header__brand:link,
.pl-shell-header__brand:visited {
  display: flex;
  align-items: center;
  gap: var(--pl-gap-sm);
  min-width: 0;
  text-decoration: none;
  font-style: normal;
  color: var(--pl-text);
}

.pl-shell-header__mark {
  width: 1.5rem;
  height: 1.5rem;
  flex: none;
}

.pl-shell-header__wordmark {
  font-weight: 600;
  letter-spacing: 0.04em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pl-shell-header__brand:hover .pl-shell-header__wordmark {
  color: var(--pl-accent);
}

/* The site, the language and the account are one cluster on the right, and the
   nav no longer pushes itself there — the two game destinations stay next to
   the mark, where "where am I" is answered, and everything peripheral sits at
   the far edge. */
.pl-shell-header__aside {
  display: flex;
  align-items: center;
  gap: var(--pl-gap-xs);
  margin-left: auto;
}

.pl-shell-header__aside > * {
  height: var(--pl-bar-control);
}

/* The switcher wears a bordered slab in the marketing header, where it stands
   among other bordered things. Here it would be the loudest object in a bar of
   flat ghost type — for the control a player touches least. Same toggle,
   stripped to the two letters; the current one is what carries the state. */
/* The class lands on the switcher's own root, which already carries .lang and
   a rule of equal weight — hence both classes here, so this wins on
   specificity rather than on stylesheet order. */
.pl-shell-header__lang.lang {
  align-items: center;
  height: auto;
  border-color: transparent;
  background: transparent;
}

.pl-shell-header__lang :deep(.lang__option.is-current) {
  background: transparent;
  color: var(--pl-accent);
}

.pl-shell-header__nav {
  display: flex;
  align-items: center;
  gap: var(--pl-gap-sm);
  margin-left: var(--pl-gap);
}

.pl-shell-header__nav > * {
  height: var(--pl-bar-control);
  padding-inline: var(--pl-gap);
}

/* Same trap as .pl-menu__item, and it predates this file's menus: global.css
   paints `a:visited` in the marketing accent, which outranks .pl-btn's own
   colour. It made the game-master link look deliberately highlighted next to
   the lobby chip — it was only visited. Guarded here rather than on .pl-btn
   itself, because adding a pseudo-class there would outrank every button
   variant's colour and repaint primary buttons across the app. */
.pl-shell-header__nav a:link,
.pl-shell-header__nav a:visited {
  color: var(--pl-text);
  font-style: normal;
}

/* Where you are, said at the size of the controls it stands among — but flat
   and unlit, because it is a label and not something to press. */
.pl-shell-header__here {
  display: inline-flex;
  align-items: center;
  background: transparent;
  border-color: var(--pl-border-dim);
  color: var(--pl-text-muted);
}

/* Identity is not navigation, so it stands on the far side of a rule rather
   than in the same run of buttons. */
.pl-shell-header__account {
  position: relative;
  display: flex;
  align-items: center;
  align-self: stretch;
  padding-left: var(--pl-gap);
  margin-left: var(--pl-gap-xs);
  border-left: 1px solid var(--pl-border-dim);
}

/* The account control reads as a person, not as another ghost button: no
   slab behind it, the name set like the rest of the bar, and the portrait
   carrying whatever emphasis the control needs. */
.pl-shell-header__id {
  height: var(--pl-bar-control);
  gap: var(--pl-gap-sm);
  padding: 0 var(--pl-gap-xs) 0 var(--pl-gap-sm);
  border-color: transparent;
  background: transparent;
}

.pl-shell-header__name {
  max-width: 12rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Narrow enough and the name is what gives way — the portrait alone still
   says whose session this is, and the nav still fits beside it. */
/* Above the fold-point the account menu carries only the account. */
.pl-shell-header__folded {
  display: none;
}

@media (max-width: 700px) {
  .pl-shell-header__name,
  .pl-shell-header__wordmark {
    display: none;
  }

  /* Four controls plus a portrait need 480px at their narrowest — more than a
     phone has. The two that are not the game move into the account menu. */
  .pl-shell-header__aside {
    display: none;
  }

  .pl-shell-header__folded {
    display: block;
  }

  .pl-shell-header__lang-row {
    display: flex;
  }

  .pl-shell-header__nav {
    margin-left: auto;
  }

  .pl-shell-header__id {
    padding-left: var(--pl-gap-xs);
  }

  .pl-shell-header__account {
    padding-left: var(--pl-gap-sm);
  }
}

/* The ring is drawn on the wrapper rather than the image, so a portrait and a
   monogram wear exactly the same one. */
.pl-shell-header__face {
  display: flex;
  border-radius: 50%;
  padding: 2px;
  background: var(--pl-surface-2);
  box-shadow: 0 0 0 1px var(--pl-border);
  transition: box-shadow 0.15s ease;
}

.pl-shell-header__id:hover .pl-shell-header__face,
.pl-shell-header__id[aria-expanded="true"] .pl-shell-header__face {
  box-shadow: 0 0 0 1px var(--pl-accent), 0 0 12px -2px var(--pl-accent);
}

/* Sized off the control rather than off --pl-avatar-sm, so the portrait keeps
   its margin inside the bar instead of filling it edge to edge. */
.pl-shell-header__portrait {
  width: calc(var(--pl-bar-control) - 0.5rem);
  border: 0;
  border-radius: 50%;
}

.pl-shell-header__menu {
  position: absolute;
  top: 100%;
  right: 0;
  min-width: 12rem;
  z-index: var(--z-overlay);
  /* Clears the bar's own padding, so the panel hangs off the bar's bottom edge
     rather than out of the middle of it. */
  margin-top: var(--pl-gap);
  padding: var(--pl-gap-xs);
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: var(--pl-gap-xs);
}

/* No `display` here: .pl-shell-header__folded hides this row above the
   fold-point, and a display of equal specificity declared later would undo
   that. The media query below is what turns it on. */
.pl-shell-header__lang-row {
  justify-content: center;
  padding: var(--pl-gap-xs) 0;
}
</style>
